import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import CloseIcon from '@mui/icons-material/Close'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import { Button, Tooltip } from '@mui/material'
import React from 'react'
import { Link } from 'react-router-dom'
import GrantContent from '../../../components/GrantContent'
import type { SearchReciboDTO } from '../../../model/kartera-api-model'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import * as Permisos from '../../../utils/Permisos'
import {
    BITACORA_FILTERS,
    chipClass,
    fiscalLabel,
    formatActivityDate,
    formatCount,
    formatSpokenDate,
    initials,
    labelize,
    money,
    NOTE_TYPES,
    noteMeta,
    RamoMark,
    statusKey,
} from '../common/customer-common'
import { siniestroChip } from '../../claims/common/claim-common'
import { fileExt, isOverdue, lateDays, OPEN_POLIZA, PAID_RECIBO, useCustomerViewModel } from './customer-view-model'
import type { CountKey, SectionState, SectionStatus, ViewTab } from './customer-view-model'

const SIN_STEPS = ['Reportado', 'En ajuste', 'Dictamen', 'Pagado']
const SIN_STEP: Record<string, number> = { captura: 1, faltante: 1, enviado: 2, aceptado: 3, finalizado: 4, rechazado: 2 }
const TABS: Array<{ id: ViewTab; label: string; countKey?: CountKey }> = [
    { id: 'resumen', label: 'Resumen' },
    { id: 'polizas', label: 'Pólizas', countKey: 'polizas' },
    { id: 'primas', label: 'Primas y pagos' },
    { id: 'siniestros', label: 'Siniestros', countKey: 'siniestros' },
    { id: 'docs', label: 'Documentos', countKey: 'docs' },
    { id: 'bitacora', label: 'Bitácora', countKey: 'bitacora' },
]

const extTone = (ext: string) => {
    if (ext === 'PDF') return 'bg-k-danger-soft text-k-danger-ink'
    if (['JPG', 'JPEG', 'PNG', 'JPE'].includes(ext)) return 'bg-tone-blue text-tone-blue-ink'
    if (ext === 'XLS' || ext === 'XLSX') return 'bg-tone-teal text-tone-teal-ink'
    return 'bg-k-line text-k-ink-2'
}

const polizaChip = (status?: string | null) => {
    const value = statusKey(status)
    if (OPEN_POLIZA.has(value)) return 'bg-k-ok-soft text-k-ok'
    if (['vencida', 'cancelada', 'precancelada', 'desprotegido', 'eliminada', 'terminada'].includes(value)) return 'bg-k-danger-soft text-k-danger-ink'
    if (value === 'renovada') return 'bg-k-line text-k-ink-2'
    return 'bg-tone-orange text-tone-orange-ink'
}

const reciboChip = (recibo: SearchReciboDTO) => {
    const value = statusKey(recibo.status)
    if (PAID_RECIBO.includes(value)) return { label: 'Pagado', className: 'bg-k-ok-soft text-k-ok' }
    if (value === 'cancelado') return { label: 'Cancelado', className: 'bg-k-line text-k-ink-2' }
    if (isOverdue(recibo)) return { label: 'Vencido', className: 'bg-k-danger-soft text-k-danger-ink' }
    return { label: 'Por pagar', className: 'bg-tone-orange text-tone-orange-ink' }
}

const moneyWhole = (value?: number | null, code?: string | null) => money(value, code, 0)

const displayValue = (status: SectionStatus, value: string) => {
    if (status === 'ready') return value
    if (status === 'loading' || status === 'idle') return '…'
    return '—'
}

const IconButton = ({ href, label, children, disabled }: { href: string | null; label: string; children: React.ReactNode; disabled?: boolean }) => {
    const className = `inline-flex h-11 w-11 items-center justify-center rounded-xl border border-k-line-strong text-k-ink ${disabled ? 'pointer-events-none opacity-40' : 'hover:bg-k-hover'}`
    if (!href || disabled) return <span className={className} aria-label={label}>{children}</span>
    return (
        <a href={href} aria-label={label} target={href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" className={className}>
            {children}
        </a>
    )
}

const Diamond = ({ color }: { color: string }) => (
    <span className="inline-block h-2.5 w-2.5 rotate-45 rounded-[3px]" style={{ background: color }} />
)

const Pulse = ({ className }: { className: string }) => <div className={`animate-pulse rounded bg-k-line ${className}`} />

const EmptyState = ({ title, hint }: { title: string; hint?: string }) => (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-k-line px-4 py-10 text-center">
        <p className="m-0 text-sm font-bold text-k-ink">{title}</p>
        {hint && <p className="mb-0 mt-1 text-xs text-k-muted">{hint}</p>}
    </div>
)

const SectionMessage = ({ section, emptyTitle, emptyHint, children }: { section: SectionState; emptyTitle: string; emptyHint: string; children?: React.ReactNode }) => {
    if (section.status === 'loading' || section.status === 'idle') return <Pulse className="h-24 w-full" />
    if (section.status === 'denied') return <EmptyState title="Sin permiso" hint="Tu usuario no puede ver esta sección." />
    if (section.status === 'error') return <p className="m-0 text-sm text-k-danger-ink">{section.error}</p>
    return <>{children || <EmptyState title={emptyTitle} hint={emptyHint} />}</>
}

const Fact = ({ label, children }: { label: string; children?: React.ReactNode }) => (
    <>
        <dt className="text-k-muted">{label}</dt>
        <dd className="m-0 break-words font-semibold text-k-ink">{children || '—'}</dd>
    </>
)

const CustomerView = () => {
    const {
        cliente,
        loading,
        error,
        tab,
        sections,
        polizas,
        recibos,
        siniestros,
        docFilter,
        noteType,
        note,
        savingNote,
        bitacoraFilter,
        uploading,
        toast,
        name,
        moral,
        phone,
        email,
        whatsapp,
        domicilioTexto,
        sinceYear,
        vigentes,
        activas,
        porRenovar,
        canceladas,
        primaAnual,
        vencidos,
        saldo,
        comision,
        year,
        currency,
        years,
        maxYear,
        growth,
        eventos,
        docFilters,
        visibleDocs,
        visibleNotes,
        notePlaceholder,
        counts,
        setTab,
        setDocFilter,
        setNoteType,
        setNote,
        setBitacoraFilter,
        dismissToast,
        reload,
        addNote,
        openFile,
        uploadFiles,
        editCliente,
    } = useCustomerViewModel()

    const polizasReady = sections.polizas.status === 'ready'
    const recibosReady = sections.recibos.status === 'ready'
    const kpis = [
        { label: 'Pólizas vigentes', value: displayValue(sections.polizas.status, formatCount(vigentes.length)), sub: polizasReady ? `${formatCount(canceladas)} canceladas · ${formatCount(porRenovar.length)} por renovar` : ' ', color: '#3E6BE6', subClass: 'text-k-muted' },
        { label: 'Prima anual', value: displayValue(sections.polizas.status, moneyWhole(primaAnual, currency)), sub: polizasReady ? (growth == null ? 'De las pólizas en vigor' : `${growth >= 0 ? '▲' : '▼'} ${Math.abs(growth)}% en el periodo`) : ' ', color: '#8B3FC9', subClass: growth != null && growth > 0 ? 'text-k-ok' : 'text-k-muted' },
        { label: 'Saldo pendiente', value: displayValue(sections.recibos.status, moneyWhole(saldo, currency)), sub: recibosReady ? (vencidos.length ? `${formatCount(vencidos.length)} recibo${vencidos.length === 1 ? '' : 's'} con atraso` : 'Sin recibos vencidos') : ' ', color: '#EC3B6C', subClass: vencidos.length ? 'text-k-danger-ink' : 'text-k-muted' },
        { label: 'Siniestros', value: displayValue(sections.siniestros.status, formatCount(siniestros.length)), sub: sections.siniestros.status === 'ready' ? 'Registrados en el expediente' : ' ', color: '#14A79F', subClass: 'text-k-muted' },
        { label: 'Comisión generada', value: displayValue(sections.recibos.status, moneyWhole(comision, currency)), sub: recibosReady ? `Acumulado ${year}` : ' ', color: '#FF7A3D', subClass: 'text-k-muted' },
    ]

    return (
        <KarteraAdminTemplate loading={false} padded={false} title={name ? `${name} · Cliente` : 'Cliente'}>
            <div className="flex flex-col gap-[18px] px-[clamp(16px,2.5vw,32px)] pb-10 pt-6">
                <Link to="/clientes" className="inline-flex min-h-7 items-center gap-1.5 self-start text-[13px] text-k-muted no-underline hover:text-k-ink">
                    <ChevronLeftIcon style={{ fontSize: 16 }} />
                    Clientes
                </Link>

                {loading && (
                    <div className="flex flex-col gap-[18px]" aria-busy="true" aria-label="Cargando cliente">
                        <div className="flex items-center gap-5 rounded-[20px] border border-k-line bg-k-surface px-6 py-[22px]">
                            <Pulse className="h-[68px] w-[68px] rounded-[20px]" />
                            <div className="flex-1">
                                <Pulse className="h-7 w-72" />
                                <Pulse className="mt-3 h-4 w-96 max-w-full" />
                            </div>
                        </div>
                        <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3.5">
                            {[1, 2, 3, 4, 5].map((item) => <Pulse key={item} className="h-[108px] rounded-[18px]" />)}
                        </div>
                    </div>
                )}

                {error && !loading && (
                    <div className="rounded-lg border border-k-danger/30 bg-k-danger-soft p-4">
                        <div className="flex items-center gap-2">
                            <ErrorOutlineIcon className="text-k-danger" style={{ fontSize: 20 }} />
                            <p className="m-0 text-sm text-k-danger-ink">{error}</p>
                        </div>
                        <Button className="mt-3" variant="contained" onClick={reload}>Reintentar</Button>
                    </div>
                )}

                {cliente && !loading && (
                    <>
                        <section className="flex flex-wrap items-center gap-5 rounded-[20px] border border-k-line bg-k-surface px-6 py-[22px]">
                            <div className="flex h-[68px] w-[68px] shrink-0 items-center justify-center rounded-[20px] text-2xl font-extrabold tracking-[-0.02em] text-white" style={{ background: 'linear-gradient(135deg, #1FC8C1, #3E6BE6 50%, #8B3FC9)' }}>
                                {initials(name)}
                            </div>
                            <div className="flex min-w-[280px] flex-1 flex-col gap-2">
                                <h1 className="m-0 text-[26px] font-extrabold tracking-[-0.03em] text-k-ink">{name || 'Cliente'}</h1>
                                <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1.5 text-[13px] text-k-muted">
                                    <span className="font-semibold tabular-nums text-k-ink">{cliente.rfc || 'Sin RFC'}</span>
                                    <span>Persona {moral ? 'moral' : 'física'}</span>
                                    {sinceYear && <span>Cliente desde {sinceYear}</span>}
                                    <span>Expediente {cliente.id}</span>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    <span className="rounded-full bg-k-accent-soft px-2.5 py-[3px] text-xs font-bold text-k-accent-ink">{fiscalLabel(cliente.fiscal)}</span>
                                    <span className={`rounded-full px-2.5 py-[3px] text-xs font-bold ${statusKey(cliente.status) === 'activo' ? 'bg-tone-teal text-tone-teal-ink' : 'bg-k-line text-k-ink-2'}`}>{labelize(cliente.status) || '—'}</span>
                                    {vencidos.length > 0 && (
                                        <span className="rounded-full bg-k-danger-soft px-2.5 py-[3px] text-xs font-bold text-k-danger-ink">
                                            {formatCount(vencidos.length)} recibo{vencidos.length === 1 ? '' : 's'} vencido{vencidos.length === 1 ? '' : 's'}
                                        </span>
                                    )}
                                    {cliente.esRobot && <span className="rounded-full bg-tone-orange px-2.5 py-[3px] text-xs font-bold text-tone-orange-ink">Por validar</span>}
                                </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                <IconButton href={phone ? `tel:${phone}` : null} label="Llamar al cliente" disabled={!phone}>
                                    <PhoneOutlinedIcon style={{ fontSize: 18 }} />
                                </IconButton>
                                <IconButton href={whatsapp ? `https://wa.me/${whatsapp}` : null} label="Escribir por WhatsApp" disabled={!whatsapp}>
                                    <WhatsAppIcon style={{ fontSize: 18 }} />
                                </IconButton>
                                <IconButton href={email ? `mailto:${email}` : null} label="Enviar correo" disabled={!email}>
                                    <EmailOutlinedIcon style={{ fontSize: 18 }} />
                                </IconButton>
                                <GrantContent permisos={[Permisos.CLIENTES_W]}>
                                    <button type="button" className="inline-flex h-11 items-center rounded-xl bg-k-primary px-[18px] text-sm font-bold text-white hover:brightness-105" onClick={editCliente}>
                                        Editar cliente
                                    </button>
                                </GrantContent>
                            </div>
                        </section>

                        <section aria-label="Indicadores del cliente" className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3.5">
                            {kpis.map((item) => (
                                <div key={item.label} className="flex flex-col gap-2 rounded-[18px] border border-k-line bg-k-surface px-[18px] py-4">
                                    <span className="flex items-center gap-2 text-[13px] font-semibold text-k-muted">
                                        <Diamond color={item.color} />
                                        {item.label}
                                    </span>
                                    <span className="text-2xl font-bold tabular-nums tracking-[-0.02em] text-k-ink">{item.value}</span>
                                    <span className={`text-xs font-semibold ${item.subClass}`}>{item.sub}</span>
                                </div>
                            ))}
                        </section>

                        <div className="flex flex-col rounded-[20px] border border-k-line bg-k-surface">
                            <div role="tablist" aria-label="Secciones del cliente" className="flex gap-0.5 overflow-x-auto border-b border-k-line px-3">
                                {TABS.map((item) => {
                                    const selected = tab === item.id
                                    const count = item.countKey ? counts[item.countKey] : null
                                    return (
                                        <button
                                            key={item.id}
                                            type="button"
                                            role="tab"
                                            aria-selected={selected}
                                            className={`inline-flex min-h-[52px] items-center gap-2 whitespace-nowrap border-0 border-b-[3px] bg-transparent px-3.5 text-sm ${selected ? 'border-k-primary font-extrabold text-k-ink' : 'border-transparent font-semibold text-k-muted'}`}
                                            onClick={() => setTab(item.id)}
                                        >
                                            {item.label}
                                            {count != null && (
                                                <span className={`inline-flex h-5 min-w-[22px] items-center justify-center rounded-[10px] px-1.5 text-xs font-bold ${selected ? 'bg-k-accent-soft text-k-accent-ink' : 'bg-k-line text-k-ink-2'}`}>
                                                    {count}
                                                </span>
                                            )}
                                        </button>
                                    )
                                })}
                            </div>

                            <div role="tabpanel" className="px-6 py-[22px]">
                                {tab === 'resumen' && (
                                    <div className="flex flex-wrap items-start gap-5">
                                        <div className="flex min-w-0 flex-[999_1_460px] flex-col gap-5">
                                            <div className="flex flex-col gap-2.5">
                                                <div className="flex items-baseline justify-between">
                                                    <h2 className="m-0 text-base font-extrabold text-k-ink">Pólizas activas</h2>
                                                    <button type="button" className="min-h-8 border-0 bg-transparent text-[13px] font-bold text-k-primary" onClick={() => setTab('polizas')}>
                                                        {polizasReady ? `Ver las ${formatCount(polizas.length)}` : 'Ver pólizas'}
                                                    </button>
                                                </div>
                                                <SectionMessage section={sections.polizas} emptyTitle="Sin pólizas activas" emptyHint="Este cliente aún no tiene pólizas en vigor.">
                                                    {activas.length === 0
                                                        ? <EmptyState title="Sin pólizas activas" hint="Las pólizas canceladas o vencidas siguen en la pestaña Pólizas." />
                                                        : activas.slice(0, 5).map((poliza) => (
                                                            <Link key={poliza.polizaId} to={`/polizas/${poliza.polizaId}`} className="flex flex-wrap items-center gap-x-3.5 gap-y-2 rounded-[14px] border border-k-line px-3.5 py-3 text-k-ink no-underline hover:bg-k-hover">
                                                                <RamoMark ramo={poliza.ramo} boxed />
                                                                <span className="flex min-w-[180px] flex-1 flex-col">
                                                                    <span className="text-sm font-bold">{poliza.concepto || poliza.ramo || 'Póliza'}</span>
                                                                    <span className="text-xs tabular-nums text-k-muted">{poliza.noPoliza || '—'} · {poliza.aseguradora || '—'}</span>
                                                                </span>
                                                                <span className="text-[13px] text-k-muted">Vence {formatSpokenDate(poliza.fechaVigenciaA) || '—'}</span>
                                                                <span className="min-w-24 text-right text-sm font-bold tabular-nums">{money(poliza.prima, poliza.moneda)}</span>
                                                                <span className={`rounded-full px-[9px] py-[3px] text-xs font-bold ${polizaChip(poliza.status)}`}>{labelize(poliza.status) || '—'}</span>
                                                            </Link>
                                                        ))}
                                                </SectionMessage>
                                            </div>
                                        </div>
                                        <div className="flex min-w-0 flex-[1_1_280px] flex-col gap-4">
                                            <div className="flex flex-col gap-3 rounded-2xl border border-k-line px-[18px] py-4">
                                                <h2 className="m-0 text-[15px] font-extrabold">Contacto</h2>
                                                <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3.5 gap-y-[9px] text-[13px]">
                                                    {cliente.alias && <Fact label="Alias">{cliente.alias}</Fact>}
                                                    <Fact label="Celular">{phone}</Fact>
                                                    <Fact label="Correo">{email}</Fact>
                                                </dl>
                                            </div>
                                            <div className="flex flex-col gap-3 rounded-2xl border border-k-line px-[18px] py-4">
                                                <h2 className="m-0 text-[15px] font-extrabold">Domicilio y datos fiscales</h2>
                                                <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3.5 gap-y-[9px] text-[13px]">
                                                    <Fact label="Domicilio">{domicilioTexto}</Fact>
                                                    <Fact label="RFC">{cliente.rfc}</Fact>
                                                    {!moral && <Fact label="Nacimiento">{formatSpokenDate(cliente.fecNacimiento)}</Fact>}
                                                </dl>
                                            </div>
                                            <div className="flex flex-col gap-2.5 rounded-2xl border border-k-line px-[18px] py-4">
                                                <h2 className="m-0 text-[15px] font-extrabold">Próximos eventos</h2>
                                                {eventos.length === 0 && <p className="m-0 text-[13px] text-k-muted">No hay vencimientos ni pagos próximos.</p>}
                                                {eventos.map((item) => (
                                                    <div key={`${item.t}-${item.time}`} className="flex items-center gap-3">
                                                        <span className={`flex w-11 shrink-0 flex-col items-center rounded-[10px] py-1 leading-none ${item.tone}`}>
                                                            <span className="text-base font-extrabold">{item.d}</span>
                                                            <span className="text-[10px] font-bold uppercase">{item.m}</span>
                                                        </span>
                                                        <span className="text-[13px] font-semibold text-k-ink">{item.t}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {tab === 'polizas' && (
                                    <SectionMessage section={sections.polizas} emptyTitle="No hay pólizas" emptyHint="Este cliente aún no tiene pólizas registradas.">
                                        {polizas.length === 0
                                            ? <EmptyState title="No hay pólizas" hint="Este cliente aún no tiene pólizas registradas." />
                                            : (
                                                <div className="overflow-x-auto">
                                                    <table className="w-full min-w-[860px] border-collapse text-sm">
                                                        <thead>
                                                            <tr className="text-left text-xs font-semibold uppercase tracking-[0.04em] text-k-muted">
                                                                <th className="pb-3 pr-2.5 font-semibold">Póliza</th>
                                                                <th className="px-2.5 pb-3 font-semibold">Ramo</th>
                                                                <th className="px-2.5 pb-3 font-semibold">Aseguradora</th>
                                                                <th className="px-2.5 pb-3 font-semibold">Vigencia</th>
                                                                <th className="px-2.5 pb-3 font-semibold">Pago</th>
                                                                <th className="px-2.5 pb-3 text-right font-semibold">Prima anual</th>
                                                                <th className="pb-3 pl-2.5 font-semibold">Estatus</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {polizas.map((poliza) => (
                                                                <tr key={poliza.polizaId} className="border-t border-k-line-soft">
                                                                    <td className="py-[13px] pr-2.5">
                                                                        <Link to={`/polizas/${poliza.polizaId}`} className="inline-flex items-center gap-2.5 font-bold tabular-nums text-k-ink no-underline hover:text-k-primary">
                                                                            <RamoMark ramo={poliza.ramo} />
                                                                            {poliza.noPoliza || '—'}
                                                                        </Link>
                                                                    </td>
                                                                    <td className="px-2.5 py-[13px]">{poliza.ramo || poliza.concepto || '—'}</td>
                                                                    <td className="px-2.5 py-[13px] text-k-muted">{poliza.aseguradora || '—'}</td>
                                                                    <td className="whitespace-nowrap px-2.5 py-[13px] text-[13px]">{formatSpokenDate(poliza.fechaVigenciaDe) || '—'} – {formatSpokenDate(poliza.fechaVigenciaA) || '—'}</td>
                                                                    <td className="px-2.5 py-[13px] text-k-muted">{labelize(poliza.formaPago) || '—'}</td>
                                                                    <td className="px-2.5 py-[13px] text-right tabular-nums">{money(poliza.prima, poliza.moneda)}</td>
                                                                    <td className="py-[13px] pl-2.5">
                                                                        <span className={`whitespace-nowrap rounded-full px-[9px] py-[3px] text-xs font-bold ${polizaChip(poliza.status)}`}>{labelize(poliza.status) || '—'}</span>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                        <tfoot>
                                                            <tr className="border-t-2 border-k-ink font-extrabold">
                                                                <td className="pt-3.5 pr-2.5" colSpan={5}>Prima anual de pólizas vigentes</td>
                                                                <td className="px-2.5 pt-3.5 text-right tabular-nums">{money(primaAnual, currency)}</td>
                                                                <td />
                                                            </tr>
                                                        </tfoot>
                                                    </table>
                                                </div>
                                            )}
                                    </SectionMessage>
                                )}

                                {tab === 'primas' && (
                                    <div className="flex flex-wrap items-start gap-6">
                                        <div className="flex min-w-0 flex-[1_1_300px] flex-col gap-3.5">
                                            <h2 className="m-0 text-base font-extrabold">Prima anual por año</h2>
                                            {years.length === 0 && <EmptyState title="Sin primas" hint="No hay pólizas con vigencia para armar el historial." />}
                                            {years.length > 0 && (
                                                <>
                                                    <div className="flex h-[190px] items-end gap-3 border-b border-k-line">
                                                        {years.map(([itemYear, value], index) => (
                                                            <div key={itemYear} className="flex h-full flex-1 flex-col items-center justify-end gap-1.5">
                                                                <span className="text-[11px] font-bold tabular-nums text-k-muted">{moneyWhole(value, currency)}</span>
                                                                <div className="w-full max-w-11 rounded-t-lg" style={{ height: Math.max(8, Math.round((value / maxYear) * 160)), background: index === years.length - 1 ? '#4B44D8' : '#CFCBF7' }} />
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <div className="flex gap-3">
                                                        {years.map(([itemYear]) => <span key={itemYear} className="flex-1 text-center text-xs font-semibold text-k-muted">{itemYear}</span>)}
                                                    </div>
                                                    {growth != null && (
                                                        <p className="m-0 text-[13px] text-k-muted">
                                                            La prima {growth >= 0 ? 'creció' : 'bajó'} <strong className={growth >= 0 ? 'text-k-ok' : 'text-k-danger-ink'}>{Math.abs(growth)}%</strong> desde {years[0][0]}.
                                                        </p>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                        <div className="flex min-w-0 flex-[999_1_460px] flex-col gap-3">
                                            <div className="flex items-baseline justify-between gap-2.5">
                                                <h2 className="m-0 text-base font-extrabold">Recibos recientes</h2>
                                                <GrantContent permisos={[Permisos.POLIZAS_COBRANZA]}>
                                                    <Link to="/cxc" className="text-[13px] font-bold text-k-primary no-underline">Ir a cobranza</Link>
                                                </GrantContent>
                                            </div>
                                            <SectionMessage section={sections.recibos} emptyTitle="Sin recibos" emptyHint="Este cliente aún no tiene recibos.">
                                                {recibos.length === 0
                                                    ? <EmptyState title="Sin recibos" hint="Este cliente aún no tiene recibos." />
                                                    : recibos.slice(0, 6).map((recibo) => {
                                                        const chip = reciboChip(recibo)
                                                        const paid = PAID_RECIBO.includes(statusKey(recibo.status))
                                                        const fecha = paid
                                                            ? `Pagado ${formatSpokenDate(recibo.fecPago) || '—'}`
                                                            : isOverdue(recibo)
                                                                ? `Venció ${formatSpokenDate(recibo.fecVence) || '—'} · ${lateDays(recibo)} días de atraso`
                                                                : `Vence ${formatSpokenDate(recibo.fecVence) || '—'}`
                                                        return (
                                                            <div key={recibo.id} className="flex flex-wrap items-center gap-x-3.5 gap-y-1.5 border-t border-k-line-soft py-[11px] text-[13px]">
                                                                <span className="flex min-w-[200px] flex-1 flex-col">
                                                                    <span className="text-sm font-bold tabular-nums">{recibo.poliza?.noPoliza || 'Recibo'} · Recibo {recibo.serie || '—'}/{recibo.totalSerie || '—'}</span>
                                                                    <span className="text-k-muted">{fecha}</span>
                                                                </span>
                                                                <span className="min-w-[100px] text-right font-bold tabular-nums">{money(recibo.total, currency)}</span>
                                                                <span className={`min-w-[72px] rounded-full px-[9px] py-[3px] text-center text-xs font-bold ${chip.className}`}>{chip.label}</span>
                                                            </div>
                                                        )
                                                    })}
                                            </SectionMessage>
                                        </div>
                                    </div>
                                )}

                                {tab === 'siniestros' && (
                                    <div className="flex flex-col gap-3.5">
                                        <div className="flex flex-wrap items-center justify-between gap-2.5">
                                            <span className="text-sm text-k-muted">
                                                {sections.siniestros.status === 'ready' ? `${formatCount(siniestros.length)} siniestro${siniestros.length === 1 ? '' : 's'} en el expediente` : 'Siniestros del cliente'}
                                            </span>
                                            <GrantContent permisos={[Permisos.SINIESTROS_W]}>
                                                <Link to="/siniestros/nuevo" className="inline-flex h-11 items-center rounded-xl border border-k-line-strong px-4 text-sm font-bold text-k-ink no-underline hover:bg-k-hover">
                                                    Reportar siniestro
                                                </Link>
                                            </GrantContent>
                                        </div>
                                        <SectionMessage section={sections.siniestros} emptyTitle="Sin siniestros" emptyHint="Este cliente no tiene siniestros registrados.">
                                            {siniestros.length === 0
                                                ? <EmptyState title="Sin siniestros" hint="Este cliente no tiene siniestros registrados." />
                                                : siniestros.map((item) => {
                                                    const step = SIN_STEP[statusKey(item.status)] || 1
                                                    const doneColor = ['aceptado', 'finalizado'].includes(statusKey(item.status)) ? '#14A79F' : '#4B44D8'
                                                    return (
                                                        <div key={item.id} className="flex flex-col gap-3.5 rounded-2xl border border-k-line px-[18px] py-4">
                                                            <div className="flex flex-wrap items-start justify-between gap-2.5">
                                                                <div className="flex flex-col gap-[3px]">
                                                                    <span className="text-[15px] font-extrabold">{item.descripcion || item.ramo || 'Siniestro'}</span>
                                                                    <span className="text-[13px] tabular-nums text-k-muted">{item.noSiniestro || '—'} · Póliza {item.noPoliza || '—'} · {formatSpokenDate(item.fechaSiniestro) || '—'}</span>
                                                                </div>
                                                                <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${siniestroChip(item.status)}`}>{labelize(item.status) || '—'}</span>
                                                            </div>
                                                            <div className="grid grid-cols-4 gap-1.5">
                                                                {SIN_STEPS.map((label, index) => (
                                                                    <div key={label} className="flex flex-col gap-1.5">
                                                                        <span className="h-1.5 rounded-[3px]" style={{ background: index < step ? doneColor : 'rgb(var(--k-line))' }} />
                                                                        <span className={`text-xs font-semibold ${index < step ? 'text-k-ink' : 'text-k-muted'}`}>{label}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            {item.fechaEstimadaResolucion && (
                                                                <span className="text-[13px] text-k-muted">Resolución estimada <strong className="text-k-ink">{formatSpokenDate(item.fechaEstimadaResolucion)}</strong></span>
                                                            )}
                                                        </div>
                                                    )
                                                })}
                                        </SectionMessage>
                                    </div>
                                )}

                                {tab === 'docs' && (
                                    <div className="flex flex-col gap-4">
                                        <div className="flex flex-wrap items-center justify-between gap-2.5">
                                            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filtrar documentos">
                                                {docFilters.map((item) => {
                                                    const selected = docFilter === item.id
                                                    return (
                                                        <button
                                                            key={item.id}
                                                            type="button"
                                                            aria-pressed={selected}
                                                            className={`min-h-[38px] rounded-full px-3.5 text-[13px] ${selected ? 'border border-k-ink bg-k-ink font-bold text-k-surface' : 'border border-k-line-strong bg-k-surface font-semibold text-k-ink'}`}
                                                            onClick={() => setDocFilter(item.id)}
                                                        >
                                                            {item.label}
                                                        </button>
                                                    )
                                                })}
                                            </div>
                                            <GrantContent permisos={[Permisos.ADJUNTOS_W]}>
                                                <label className={`inline-flex h-11 cursor-pointer items-center gap-2 rounded-xl bg-k-primary px-4 text-sm font-bold text-white ${uploading ? 'opacity-60' : ''}`}>
                                                    <UploadFileOutlinedIcon style={{ fontSize: 16 }} />
                                                    {uploading ? 'Subiendo…' : 'Subir documento'}
                                                    <input type="file" className="sr-only" disabled={uploading} onChange={(event) => { uploadFiles(event.target.files); event.target.value = '' }} />
                                                </label>
                                            </GrantContent>
                                        </div>
                                        <SectionMessage section={sections.adjuntos} emptyTitle="Sin documentos" emptyHint="Este cliente aún no tiene archivos.">
                                            {visibleDocs.length === 0
                                                ? <EmptyState title="Sin documentos" hint={docFilter === 'todos' ? 'Este cliente aún no tiene archivos.' : 'No hay archivos en este filtro.'} />
                                                : (
                                                    <div className="grid grid-cols-[repeat(auto-fill,minmax(420px,1fr))] gap-3">
                                                        {visibleDocs.map((item) => {
                                                            const ext = fileExt(item.archivo)
                                                            const fileName = item.archivo || 'Archivo'
                                                            return (
                                                                <div key={item.id} className="flex items-center gap-3 rounded-2xl border border-k-line bg-k-surface px-3.5 py-3">
                                                                    <span className={`flex h-11 w-9 shrink-0 items-end justify-center rounded-lg pb-1.5 text-[10px] font-extrabold ${extTone(ext)}`}>{ext}</span>
                                                                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                                                                        <Tooltip title={fileName} placement="top">
                                                                            <span className="block min-w-0 truncate text-sm font-bold">{fileName}</span>
                                                                        </Tooltip>
                                                                        <span className="truncate text-xs text-k-muted">{formatActivityDate(item.fecha) || 'Adjunto del cliente'}</span>
                                                                    </span>
                                                                    <button type="button" aria-label={`Descargar ${fileName}`} className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-k-line bg-k-surface" onClick={() => openFile(item.path)}>
                                                                        <FileDownloadOutlinedIcon style={{ fontSize: 16 }} />
                                                                    </button>
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                )}
                                        </SectionMessage>
                                    </div>
                                )}

                                {tab === 'bitacora' && (
                                    <div className="flex flex-col gap-4">
                                        <GrantContent permisos={[Permisos.BITACORA_W]}>
                                            <section className="flex flex-col gap-2.5 rounded-2xl border border-k-line bg-k-surface p-3.5">
                                                <div role="radiogroup" aria-label="Tipo de registro" className="flex flex-wrap gap-1.5">
                                                    {NOTE_TYPES.map((item) => (
                                                        <button key={item.id} type="button" role="radio" aria-checked={noteType === item.id} className={chipClass(noteType === item.id)} onClick={() => setNoteType(item.id)}>
                                                            <item.Icon style={{ fontSize: 16 }} />
                                                            {item.label}
                                                        </button>
                                                    ))}
                                                </div>
                                                <label className="flex flex-col">
                                                    <span className="sr-only">Nuevo registro de bitácora</span>
                                                    <textarea rows={3} value={note} placeholder={notePlaceholder} className="resize-y rounded-xl border border-k-line-strong bg-k-bg px-3 py-2.5 text-sm text-k-ink" onChange={(event) => setNote(event.target.value)} />
                                                </label>
                                                <div className="flex items-center justify-between gap-2.5">
                                                    <span className="text-xs text-k-muted">Visible para tu equipo · se guarda con fecha</span>
                                                    <button type="button" disabled={!note.trim() || savingNote} className={`h-10 rounded-[10px] border-0 px-3.5 text-[13px] font-bold ${note.trim() && !savingNote ? 'cursor-pointer bg-k-primary text-white' : 'cursor-not-allowed bg-k-line text-k-muted-2'}`} onClick={addNote}>
                                                        {savingNote ? 'Guardando…' : 'Agregar a bitácora'}
                                                    </button>
                                                </div>
                                            </section>
                                        </GrantContent>
                                        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filtrar bitácora">
                                            {BITACORA_FILTERS.map((item) => {
                                                const Icon = NOTE_TYPES.find((type) => type.filter === item.id)?.Icon
                                                return (
                                                    <button key={item.id} type="button" aria-pressed={bitacoraFilter === item.id} className={chipClass(bitacoraFilter === item.id)} onClick={() => setBitacoraFilter(item.id)}>
                                                        {Icon && <Icon style={{ fontSize: 16 }} />}
                                                        {item.label}
                                                    </button>
                                                )
                                            })}
                                        </div>
                                        <SectionMessage section={sections.bitacora} emptyTitle="No hay registros" emptyHint="Este cliente aún no tiene bitácora.">
                                            {visibleNotes.length === 0
                                                ? (
                                                    <EmptyState
                                                        title="No hay registros"
                                                        hint={bitacoraFilter === 'todo' ? 'Este cliente aún no tiene bitácora.' : 'No hay registros en esta categoría.'}
                                                    />
                                                )
                                                : (
                                                    <div className="flex flex-col">
                                                        {visibleNotes.map((item, index) => {
                                                            const meta = noteMeta(item.tipo)
                                                            return (
                                                                <div key={item.id || index} className="flex gap-3.5">
                                                                    <div className="flex flex-col items-center">
                                                                        <span className={`flex h-[34px] w-[34px] items-center justify-center rounded-[10px] ${meta.tone}`}>
                                                                            <meta.Icon style={{ fontSize: 16 }} />
                                                                        </span>
                                                                        {index < visibleNotes.length - 1 && <span className="my-1 w-0.5 flex-1 bg-k-line" />}
                                                                    </div>
                                                                    <div className="flex min-w-0 flex-1 flex-col gap-1 pb-[18px] pt-1">
                                                                        <div className="flex flex-wrap justify-between gap-x-2.5 gap-y-1">
                                                                            <span className="text-sm font-extrabold">{meta.label}</span>
                                                                            <span className="text-xs tabular-nums text-k-muted">{formatActivityDate(item.fecha) || '—'}</span>
                                                                        </div>
                                                                        {item.texto && <p className="m-0 rounded-xl border border-k-line bg-k-bg px-3 py-2.5 text-sm leading-normal text-k-ink-2">{item.texto}</p>}
                                                                    </div>
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                )}
                                        </SectionMessage>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
            {toast && (
                <div role="status" className="fixed bottom-6 right-6 z-[60] flex max-w-[calc(100%-48px)] items-center gap-3 rounded-xl bg-[#1B1D29] py-3 pl-4 pr-3 text-sm text-white shadow-[0_12px_32px_rgba(27,29,41,0.3)]">
                    <span className="text-[#3FD3C6]">✓</span>
                    <span>{toast}</span>
                    <button type="button" aria-label="Cerrar aviso" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border-0 bg-transparent text-white" onClick={dismissToast}>
                        <CloseIcon style={{ fontSize: 16 }} />
                    </button>
                </div>
            )}
        </KarteraAdminTemplate>
    )
}

export default CustomerView
